// src/components/promos/PromosTable.tsx
import React from "react";
import {
  FiBarChart2,
  FiEdit2,
  FiHash,
  FiPause,
  FiPlay,
  FiTrash2,
  FiZap,
} from "react-icons/fi";

import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import type { Promo, PromoStatus } from "@/api/features/promos";

/** Icon action with a hover tooltip — matches the other admin tables. */
const ActionButton: React.FC<{
  icon: React.ReactNode;
  tooltip: string;
  color: string;
  onClick: () => void;
}> = ({ icon, tooltip, color, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={tooltip}
    aria-label={tooltip}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${color}`}
  >
    {icon}
  </button>
);

export type PromosTableFilters = {
  search?: string;
  status?: PromoStatus | "";
};

const STATUS_OPTIONS: PromoStatus[] = [
  "draft",
  "active",
  "paused",
  "expired",
  "archived",
];

type Props = {
  rows: Promo[];
  loading?: boolean;
  filters: PromosTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<PromosTableFilters>>;
  onEdit: (p: Promo) => void;
  onDelete: (p: Promo) => void;
  onGenerateCodes: (p: Promo) => void;
  onSimulate: (p: Promo) => void;
  onAnalytics: (p: Promo) => void;
  onToggleStatus: (p: Promo, next: PromoStatus) => void;
};

const STATUS_STYLE: Record<PromoStatus, string> = {
  draft: "bg-gray-100 text-gray-700",
  active: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700",
  expired: "bg-rose-100 text-rose-700",
  archived: "bg-zinc-200 text-zinc-600",
};

const money = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—";
};

function discountLabel(p: Promo) {
  if (p.discountType === "percentage") {
    const cap = p.maxDiscountAmount != null ? ` (max ${money(p.maxDiscountAmount)})` : "";
    return `${money(p.discountValue)}%${cap}`;
  }
  return `${p.currencyCode ?? ""} ${money(p.discountValue)}`.trim();
}

function redemptionsLabel(p: Promo) {
  const used = p.confirmedCount + p.reservedCount;
  return p.maxRedemptions != null ? `${used} / ${p.maxRedemptions}` : `${used} / ∞`;
}

export default function PromosTable({
  rows,
  loading,
  filters,
  setFilters,
  onEdit,
  onDelete,
  onGenerateCodes,
  onSimulate,
  onAnalytics,
  onToggleStatus,
}: Props) {
  return (
    // Square bottom and the same radius as every other list card: the
    // pagination bar below completes it.
    <div className="overflow-x-auto rounded-t-2xl border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-3">
              <div className="flex items-center gap-1.5">
                <span>Promo</span>
                <ColumnFilter label="Promo">
                  <Input
                    placeholder="Contains…"
                    value={filters.search || ""}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, search: e.target.value }))
                    }
                  />
                </ColumnFilter>
              </div>
            </th>
            <th className="px-4 py-3">Discount</th>
            <th className="px-4 py-3">
              <div className="flex items-center gap-1.5">
                <span>Status</span>
                <ColumnFilter label="Status">
                  <select
                    value={filters.status || ""}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        status: e.target.value as PromoStatus | "",
                      }))
                    }
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  >
                    <option value="">All statuses</option>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </ColumnFilter>
              </div>
            </th>
            <th className="px-4 py-3">Redemptions</th>
            <th className="px-4 py-3">Budget used</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {loading ? (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-gray-400">Loading…</td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-gray-400">No promos yet.</td>
            </tr>
          ) : (
            rows.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50/60">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{p.name}</div>
                  <div className="text-xs text-gray-400">
                    {p.codeMode === "shared" ? "Shared code" : "Unique batch"} · {p.audience}
                    {p.firstOrderOnly ? " · first-order" : ""}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-700">{discountLabel(p)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[p.status]}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700">{redemptionsLabel(p)}</td>
                <td className="px-4 py-3 text-gray-700">
                  {money(p.discountGiven)}
                  {p.maxDiscountBudget != null ? ` / ${money(p.maxDiscountBudget)}` : ""}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {p.status === "active" ? (
                      <ActionButton
                        icon={<FiPause />}
                        tooltip="Pause promo"
                        color="text-amber-600 hover:bg-amber-50"
                        onClick={() => onToggleStatus(p, "paused")}
                      />
                    ) : (
                      <ActionButton
                        icon={<FiPlay />}
                        tooltip="Activate promo"
                        color="text-emerald-600 hover:bg-emerald-50"
                        onClick={() => onToggleStatus(p, "active")}
                      />
                    )}

                    {p.codeMode === "unique_batch" ? (
                      <ActionButton
                        icon={<FiHash />}
                        tooltip="Generate codes"
                        color="text-indigo-600 hover:bg-indigo-50"
                        onClick={() => onGenerateCodes(p)}
                      />
                    ) : null}

                    <ActionButton
                      icon={<FiZap />}
                      tooltip="Simulate"
                      color="text-sky-600 hover:bg-sky-50"
                      onClick={() => onSimulate(p)}
                    />
                    <ActionButton
                      icon={<FiBarChart2 />}
                      tooltip="Analytics"
                      color="text-violet-600 hover:bg-violet-50"
                      onClick={() => onAnalytics(p)}
                    />
                    <ActionButton
                      icon={<FiEdit2 />}
                      tooltip="Edit"
                      color="text-gray-600 hover:bg-gray-100"
                      onClick={() => onEdit(p)}
                    />
                    <ActionButton
                      icon={<FiTrash2 />}
                      tooltip="Delete"
                      color="text-rose-600 hover:bg-rose-50"
                      onClick={() => onDelete(p)}
                    />
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
